import React, { useContext, useState } from "react";
import { HomeContext } from "../containers/Home";
import useCheckHighlight from "../hooks/useCheckHighlight";
import useDragTaskCard from "../hooks/useDragTaskCard";
import { updateTaskItem } from "../services/api-server/tasks";
import { TTaskData } from "../types/task";
import { alertMessage } from "../utils/alert";
import {
	checkProjectTeam,
	checkSuperAdminPriviledge,
	includesIgnoreCase,
} from "../utils/utils";
import { useProjectStore } from "../stores/projectStore";
import ClosingTaskModal from "./Modal/ClosingTaskModal";
import TaskReopenModal from "../containers/TaskReopenModal";
import LateTaskModal from "./Modal/LateTaskModal";
import { TWellData } from "../types/well";
import { TOperationData } from "../types/operation";

interface ColumnInterface {
	status: string;
	droppable: boolean;
	tasks: TTaskData[];
}

function DropColumn({
	column,
	operation,
	updateColumnData,
	children,
}: {
	column: ColumnInterface;
	operation?: (TOperationData & TWellData) | null;
	updateColumnData: (initialTask: TTaskData, newTask: TTaskData) => void;
	children: React.ReactNode;
}) {
	const textHighlight = useCheckHighlight();
	const { currentTenantKey, userInfo, userRole }: any = useContext(HomeContext);

	const { project } = useProjectStore();

	const { handleDrop, handleDragOver, handleDragLeave, active } =
		useDragTaskCard();

	const [confirmCloseTaskModalOpen, setConfirmCloseTaskModalOpen] =
		useState(false);
	const [reopenModalOpen, setReopenModalOpen] = useState(false);
	const [lateTaskModalOpen, setLateTaskModalOpen] = useState(false);
	const [updatedTask, setUpdatedTask] = useState<TTaskData | null>(null);
	const [reopenReason, setReopenReason] = useState<string>("");

	// Helper function to create audit log
	const createAuditLog = (
		task: TTaskData,
		newTask: any,
		initialTask: TTaskData,
		reopenReason?: string,
		lateData?: any
	) => {
		const eventSuffix = [
			reopenReason && "(reopened)",
			lateData && "(marked as late)",
		]
			.filter(Boolean)
			.join(" ");

		return {
			level: "phase",
			level_id: task.phase_id,
			event_type: "update",
			event: `${
				newTask.task_name
			} state updated based on drag and drop activity${
				eventSuffix ? ` ${eventSuffix}` : ""
			}`,
			details: [
				{
					new_data: newTask.status,
					old_data: initialTask.status,
				},
			],
			ref_tags: {
				phase_id: newTask.phase_id,
			},
			created_on: new Date(),
			created_by: userInfo.user.email,
		};
	};

	// Helper function to handle task updates
	const performTaskUpdate = async (
		task: TTaskData,
		newTask: any,
		initialTask: TTaskData,
		updateFields: string[],
		extraAuditLog: any
	) => {
		try {
			const updatedTaskData = await updateTaskItem(
				currentTenantKey,
				task._id,
				newTask,
				initialTask,
				userInfo.user,
				updateFields,
				extraAuditLog
			);

			updateColumnData(initialTask, updatedTaskData as TTaskData);
			alertMessage("success", "Task has been successfully updated.");
		} catch (error) {
			console.log("error", error);
			alertMessage("error", "Changes are not saved. Please try again.");
		}
	};

	// Helper function to clean up late task data when moving away from Late status
	const cleanupLateTaskData = (
		newTask: any,
		initialTask: TTaskData,
		status: string
	) => {
		if (initialTask.status === "Late" && status !== "Late") {
			newTask.blocker = null;
			newTask.plan = null;
		}
	};

	const updateTaskStatus = async (task: TTaskData, status: string) => {
		const initialTask = task;
		const newTask =
			status === "Closed"
				? { ...initialTask, closed: true }
				: { ...initialTask, status: status };

		cleanupLateTaskData(newTask, initialTask, status);

		const extraAuditLog =
			newTask.closed === initialTask.closed
				? createAuditLog(task, newTask, initialTask)
				: null;

		await performTaskUpdate(
			task,
			newTask,
			initialTask,
			["status", "closed"],
			extraAuditLog
		);
	};

	const updateTaskStatusWithReopen = async (
		task: TTaskData,
		status: string,
		reopenReason?: string,
		lateData?: any
	) => {
		const initialTask = task;
		const newTask = {
			...initialTask,
			status: status,
			closed: false,
			...(reopenReason && { reopen_reason: reopenReason }),
			...(lateData && { ...lateData }),
		};

		cleanupLateTaskData(newTask, initialTask, status);

		const extraAuditLog = createAuditLog(
			task,
			newTask,
			initialTask,
			reopenReason,
			lateData
		);

		await performTaskUpdate(
			task,
			newTask,
			initialTask,
			["status", "closed", "reopen_reason", "blocker", "plan"],
			extraAuditLog
		);
	};

	const permissionCheck = (task: TTaskData) => {
		/**
		 * Only allow the following users to update the task:
		 * 1. Tenant admin
		 * 2. Super admin
		 * 3. Project team member
		 * 4. Project manager
		 */
		const responsibleParty = project?.role_assignments.filter((role: any) =>
			task.responsible_roles.includes(role.role_id)
		);
		const allowed =
			userRole.role === "tenant_admin" ||
			checkSuperAdminPriviledge(userInfo.userRole) ||
			checkProjectTeam(userInfo.user.email, responsibleParty) ||
			includesIgnoreCase(project?.project_manager, userInfo.user.email);
		return allowed;
	};

	return (
		<>
			<div
				key={column.status}
				className="project-status-container-content"
				style={{
					minWidth: "320px",
					maxWidth: "320px",
					background: active
						? "rgba(80, 106, 108, 0.49)"
						: "rgba(255, 255, 255, 0.15)",
				}}
				onDrop={(e) => {
					const task = handleDrop(e);
					setUpdatedTask(task);

					// prevent drop if the task's operation_id is not equal to operation's id
					if (task.operation_id !== operation?._id) {
						alertMessage(
							"error",
							"Tasks that do not belong to this operation or phase cannot be moved to another operation."
						);
						return;
					}

					if (!textHighlight && column.droppable === true) {
						// if column status is closed, return if the user does not have permission to update the task
						if (column.status === "Closed") {
							if (!permissionCheck(task)) {
								alertMessage(
									"error",
									"You do not have permission to update this task."
								);
								return;
							}

							// closing an open task
							if (task.closed !== true) {
								setConfirmCloseTaskModalOpen(true);
								return;
							}
						} else if (column.status !== "Closed" && task.closed === true) {
							// reopen an closed task
							setReopenModalOpen(true);
							return;
						} else if (column.status === "Late" && task.status !== "Late") {
							setLateTaskModalOpen(true);
							return;
						}
						if (task.status !== column.status && column.status !== "Closed") {
							updateTaskStatus(task, column.status);
						}
					}
				}}
				onDragOver={
					textHighlight || column.droppable === false
						? undefined
						: handleDragOver
				}
				onDragLeave={handleDragLeave}
			>
				{children}
			</div>
			<LateTaskModal
				open={lateTaskModalOpen}
				setOpen={(open: boolean) => {
					setLateTaskModalOpen(open);
					// Clear reopen reason when modal is closed
					if (!open) {
						setReopenReason("");
					}
				}}
				updateTaskStatus={(task: TTaskData, status: string) => {
					// If this is a reopen operation (reopenReason exists), use the combined save
					if (updatedTask && reopenReason) {
						updateTaskStatusWithReopen(updatedTask, status, reopenReason, {
							blocker: task.blocker,
							plan: task.plan,
						});
					} else if (updatedTask) {
						// If this is a regular move to Late (not a reopen), use the regular update
						updateTaskStatus(task, status);
					}
				}}
				taskInfo={updatedTask as TTaskData}
			/>
			<ClosingTaskModal
				open={confirmCloseTaskModalOpen}
				setOpen={setConfirmCloseTaskModalOpen}
				handleOk={() => {
					updateTaskStatus(updatedTask as TTaskData, "Closed");
					setConfirmCloseTaskModalOpen(false);
				}}
			/>
			<TaskReopenModal
				modalOpen={reopenModalOpen}
				setModalOpen={setReopenModalOpen}
				handleReopenTask={(reason: string) => {
					if (!updatedTask) return;

					setReopenReason(reason);

					// If reopening as Late, open the Late modal to collect additional data
					if (column.status === "Late") {
						setLateTaskModalOpen(true);
					} else {
						// For non-Late status, save immediately
						updateTaskStatusWithReopen(updatedTask, column.status, reason);
					}
					setReopenModalOpen(false);
				}}
				taskInfo={updatedTask}
			/>
		</>
	);
}

export default DropColumn;
